if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.flipMediaIds = null;
        this.flipMode = document.body.classList.contains('mj-page-product');
        this.boundFlipHeightSync = this.queueFlipHeightSync.bind(this);
        this.boundMediaQueryChange = this.onMediaQueryChange.bind(this);
        if (this.elements.viewer) {
          this.refreshFlipCards();
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.viewer.addEventListener('click', (event) => {
            const back = event.target.closest('.mj-product-flip__back');
            if (!back) return;
            const targetMediaId = back.dataset.sourceMediaId;
            const targetItem =
              Array.from(this.elements.viewer.querySelectorAll('.product__media-item')).find(
                (item) => item.dataset.mediaId === targetMediaId
              ) || back.closest('.product__media-item');
            const zoomButton = targetItem?.querySelector('.product__media-toggle');
            if (!zoomButton) return;
            event.preventDefault();
            event.stopPropagation();
            zoomButton.click();
          });
        if (this.flipMode) {
          this.galleryFrame = this.closest('.product__media-wrapper');
          this.galleryInfoCard = this.closest('.product')?.querySelector('.product__info-container');
          this.galleryDetails = Array.from(this.galleryInfoCard?.querySelectorAll('.mj-purchase-help__item') || []);
          this.galleryDetails.forEach((details) => details.addEventListener('toggle', this.boundFlipHeightSync));
            if (this.galleryInfoCard && this.galleryFrame && window.ResizeObserver) {
              this.galleryResizeObserver = new ResizeObserver(this.boundFlipHeightSync);
              this.galleryResizeObserver.observe(this.galleryInfoCard);
            }
            if (this.mql.addEventListener) this.mql.addEventListener('change', this.boundMediaQueryChange);
            else this.mql.addListener(this.boundMediaQueryChange);
            window.addEventListener('resize', this.boundFlipHeightSync, { passive: true });
            this.queueFlipHeightSync();
            if (document.fonts?.ready) document.fonts.ready.then(this.boundFlipHeightSync);
          }
        }
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }
      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
        this.refreshFlipCards();
      }
      disconnectedCallback() {
        this.galleryDetails?.forEach((details) => details.removeEventListener('toggle', this.boundFlipHeightSync));
        this.galleryResizeObserver?.disconnect();
        if (this.mql?.removeEventListener) this.mql.removeEventListener('change', this.boundMediaQueryChange);
        else this.mql?.removeListener(this.boundMediaQueryChange);
        window.removeEventListener('resize', this.boundFlipHeightSync);
        window.cancelAnimationFrame(this.flipHeightFrame);
      }
      onMediaQueryChange() {
        this.refreshFlipCards();
        this.queueFlipHeightSync();
      }
      queueFlipHeightSync() {
        window.cancelAnimationFrame(this.flipHeightFrame);
        this.flipHeightFrame = window.requestAnimationFrame(() => this.syncFlipHeight());
      }
      syncFlipHeight() {
        if (!this.galleryFrame || !this.galleryInfoCard) return;
        if (!this.mql.matches) {
          this.galleryFrame.style.removeProperty('--mj-product-gallery-height');
          return;
        }
        if (this.galleryDetails?.some((details) => details.open)) return;
        const height = this.galleryInfoCard.getBoundingClientRect().height;
        if (height > 0) this.galleryFrame.style.setProperty('--mj-product-gallery-height', `${Math.ceil(height)}px`);
      }
      teardownFlipCards() {
        if (!this.elements.viewer) return;
        this.elements.viewer.querySelectorAll('.product-media-container.mj-product-flip').forEach((container) => {
          const inner = container.querySelector('.mj-product-flip__inner');
          const front = inner?.querySelector('.mj-product-flip__front');
          const original = front?.firstElementChild;
          if (original) container.insertBefore(original, inner);
          inner?.remove();
          container.classList.remove('mj-product-flip');
        });
        this.elements.viewer.removeAttribute('data-mj-gallery-flip');
      }
      refreshFlipCards() {
        if (!this.elements.viewer || !this.flipMode) return;
        if (!this.mql.matches) {
          this.teardownFlipCards();
          return;
        }
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item'));
        const imageItems = items.filter((item) =>
          item.querySelector('.product-media-container.media-type-image .product__media img')
        );

        if (imageItems.length < 2) {
          this.teardownFlipCards();
          return;
        }
        if (!this.flipMediaIds) this.flipMediaIds = imageItems.map((item) => item.dataset.mediaId).filter(Boolean);
        const itemsById = new Map(imageItems.map((item) => [item.dataset.mediaId, item]));
        const orderedImageItems = this.flipMediaIds.map((id) => itemsById.get(id)).filter(Boolean);
        orderedImageItems.forEach((item, index) => {
          const container = item.querySelector('.product-media-container.media-type-image');
          const frontMedia = container?.querySelector('.product__media');
          const frame = container?.querySelector('.product__modal-opener') || frontMedia;
          const nextItem = orderedImageItems[(index + 1) % orderedImageItems.length];
          const source = nextItem?.querySelector('.product-media-container.media-type-image .product__media img');
          if (!container || !frontMedia || !frame || !source) return;

          let inner = container.querySelector('.mj-product-flip__inner');
          if (!inner) {
            inner = document.createElement('div');
            inner.className = 'mj-product-flip__inner';
            const front = document.createElement('div');
            front.className = 'mj-product-flip__front';
            container.insertBefore(inner, frame);
            inner.appendChild(front);
            front.appendChild(frame);
          }

          const front = inner.querySelector('.mj-product-flip__front');
          let back = inner.querySelector('.mj-product-flip__back');
          if (!back) {
            back = document.createElement('div');
            back.className = 'mj-product-flip__back';
            back.setAttribute('aria-hidden', 'true');
            const backMedia = frontMedia.cloneNode(false);
            backMedia.removeAttribute('id');
            backMedia.classList.add('mj-product-flip__back-media');
            back.appendChild(backMedia);
            inner.appendChild(back);
          }

          const sourceMediaId = nextItem.dataset.mediaId || String(index);
          const backMedia = back.querySelector('.mj-product-flip__back-media');
          if (!front || !backMedia) return;
          container.classList.add('mj-product-flip');
          if (back.dataset.sourceMediaId === sourceMediaId) return;

          const preview = source.cloneNode(false);
          preview.removeAttribute('id');
          preview.alt = '';
          preview.setAttribute('aria-hidden', 'true');
          preview.loading = item.classList.contains('is-active') ? 'eager' : 'lazy';
          preview.decoding = 'async';
          back.dataset.sourceMediaId = sourceMediaId;
          backMedia.replaceChildren(preview);
        });
        this.elements.viewer.dataset.mjGalleryFlip = 'ready';
      }
      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || !this.elements.thumbnails || this.elements.viewer.dataset.mjGalleryFlip !== 'ready') {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.refreshFlipCards();
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }
      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }
      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

